import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Coins, Flame, Clock, Sparkles } from "lucide-react";
import { CropSprite } from "@/components/valley/CropSprite";
import { fmtDuration } from "@/components/valley/stage";

function SeedCard({ id, s, selected, affordable, onPick }) {
  return (
    <button data-testid={`seed-option-${id}`} onClick={() => onPick(id)}
      className={`w-full text-left card-tile flex items-center gap-3 transition-colors ${selected ? "border-orange-500/70 card-flame" : "hover:border-orange-500/30"} ${affordable ? "" : "opacity-50"}`}>
      <div className="rounded-xl bg-[#3f8f31]/40 border border-white/5 p-1"><CropSprite seed={id} stage="ready" size={52} /></div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-bold">{s.name}</div>
        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs">
          <span className="flex items-center gap-1 text-sky-300"><Coins size={12} />{s.cost_cro} CRO</span>
          <span className="flex items-center gap-1 text-slate-300"><Clock size={12} />{fmtDuration(s.grow_sec)}</span>
          <span className="flex items-center gap-1 text-orange-400"><Flame size={12} />{s.yield.toLocaleString()} FLAME</span>
        </div>
        <div className="text-[10px] text-slate-500 mt-1">Harvest window {fmtDuration(s.grow_sec * 2)} · {(s.yield / s.cost_cro).toFixed(0)} FLAME per CRO</div>
      </div>
    </button>
  );
}

function DecorCard({ id, d, owned, affordable, onBuy, busy }) {
  const maxed = owned >= d.max;
  return (
    <div className="card-tile flex items-center gap-3" data-testid={`decor-option-${id}`}>
      <div className="flex-1">
        <div className="font-display font-bold">{d.name}</div>
        <div className="mt-1 flex flex-wrap gap-x-3 text-xs">
          <span className="flex items-center gap-1 text-sky-300"><Coins size={12} />{d.cost_cro} CRO</span>
          <span className="flex items-center gap-1 text-lime-300"><Sparkles size={12} />+{d.bonus_pct}% FLAME</span>
          <span className="text-slate-400">Owned {owned}/{d.max}</span>
        </div>
      </div>
      <button data-testid={`decor-buy-${id}`} disabled={maxed || !affordable || busy} onClick={() => onBuy(id)} className="btn-tactile btn-flame text-xs px-3 py-2">
        {maxed ? "Owned" : "Buy"}
      </button>
    </div>
  );
}

export const SeedShop = ({ open, onOpenChange, tab, onTab, seeds, decor, owned, selected, onPick, onBuyDecor, croBalance, bonusPct, busy }) => (
  <Sheet open={open} onOpenChange={onOpenChange}>
    <SheetContent side="right" className="bg-[#12131C] border-l border-[#2A2E45] text-slate-100 overflow-y-auto w-full sm:max-w-md" data-testid="seed-shop-drawer">
      <SheetHeader>
        <SheetTitle className="font-display text-2xl text-slate-100">Farm Shop</SheetTitle>
        <SheetDescription className="text-slate-400">Paid from your farm balance: <span className="text-sky-300 font-mono">{croBalance.toFixed(2)} CRO</span></SheetDescription>
      </SheetHeader>
      <Tabs value={tab} onValueChange={onTab} className="mt-4">
        <TabsList className="bg-black/40 w-full">
          <TabsTrigger value="seeds" className="flex-1" data-testid="shop-tab-seeds">Seeds</TabsTrigger>
          <TabsTrigger value="decor" className="flex-1" data-testid="shop-tab-decor">Decor</TabsTrigger>
        </TabsList>
        <TabsContent value="seeds">
          {[["short", "QUICK CROPS"], ["long", "LONG-TERM CROPS"]].map(([term, label]) => (
            <div key={term} className="mt-4">
              <div className="font-pixel text-[9px] text-slate-400 mb-2">{label}</div>
              {term === "long" && <p className="text-xs text-slate-500 mb-2">Longer terms cost more but earn more FLAME per CRO.</p>}
              <div className="space-y-2">
                {Object.entries(seeds).filter(([, s]) => s.term === term).map(([id, s]) => (
                  <SeedCard key={id} id={id} s={s} selected={selected === id} affordable={croBalance >= s.cost_cro} onPick={onPick} />
                ))}
              </div>
            </div>
          ))}
        </TabsContent>
        <TabsContent value="decor">
          <p className="text-xs text-slate-400 mt-4">Decorate your farm to boost every harvest. Current bonus: <span className="text-lime-300 font-bold">+{bonusPct}%</span></p>
          <div className="space-y-2 mt-3">
            {Object.entries(decor).map(([id, d]) => (
              <DecorCard key={id} id={id} d={d} owned={owned[id] || 0} affordable={croBalance >= d.cost_cro} onBuy={onBuyDecor} busy={busy} />
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </SheetContent>
  </Sheet>
);
